import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChartLineUp, Trophy, ArrowsLeftRight } from "@phosphor-icons/react";
import { compareAssets } from "../lib/api";
import SEO from "../components/SEO";

const labelColor = (s) =>
  s == null ? "#A1A1AA" : s >= 60 ? "#34C759" : s <= 40 ? "#FF3B30" : "#FFCC00";

// Renders /compare/AAPL/MSFT and /compare/AAPL-vs-MSFT
export default function Compare() {
  const navigate = useNavigate();
  const params = useParams();
  // Support both /compare/:a/:b and /compare/:pair (a-vs-b)
  let symA = params.a;
  let symB = params.b;
  if (params.pair && !symA) {
    const m = params.pair.split(/-vs-|_vs_/i);
    if (m.length === 2) { symA = m[0]; symB = m[1]; }
  }
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!symA || !symB) { setLoading(false); setErr("missing"); return; }
    setLoading(true);
    compareAssets(symA, symB)
      .then(setData)
      .catch((e) => setErr(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [symA, symB]);

  const a = data?.a;
  const b = data?.b;
  const winner = data?.winner;

  const title = `${symA} vs ${symB} — Live Sentiment Comparison`;
  const description = data
    ? `Compare ${a?.asset?.name || symA} (${symA}) vs ${b?.asset?.name || symB} (${symB}) by live sentiment, technicals, momentum, and analyst forecasts. ${symA} composite: ${Math.round(a?.composite || 0)}/100. ${symB} composite: ${Math.round(b?.composite || 0)}/100. ${winner ? `Winner: ${winner}.` : "Both tied."}`
    : `Side-by-side sentiment comparison of ${symA} vs ${symB} on Sent-i-meter.`;

  return (
    <div className="min-h-screen bg-obsidian text-white" data-testid="compare-page">
      <SEO
        title={title}
        path={`/compare/${encodeURIComponent(symA || "")}-vs-${encodeURIComponent(symB || "")}`}
        description={description}
        keywords={[
          `${symA} vs ${symB}`,
          `${symA} or ${symB}`,
          `${symA} ${symB} comparison`,
          `${symA} sentiment`,
          `${symB} sentiment`,
          "stock comparison",
        ]}
        image={`https://sent-i-meter.com/api/og/asset/${encodeURIComponent(symA || "")}.png`}
        jsonLd={data ? {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: title,
          about: [
            { "@type": "FinancialProduct", name: a?.asset?.name || symA, identifier: symA },
            { "@type": "FinancialProduct", name: b?.asset?.name || symB, identifier: symB },
          ],
        } : null}
      />
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/70 border-b border-white/10 px-4 md:px-6 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/70 hover:text-white" data-testid="compare-back">
            <ArrowLeft size={16} weight="bold" />
            <span className="overline">back</span>
          </button>
          <div className="w-px h-6 bg-white/10 mx-2" />
          <button onClick={() => navigate("/")} className="w-8 h-8 bg-white text-black flex items-center justify-center">
            <ChartLineUp size={16} weight="bold" />
          </button>
          <div className="hidden sm:block">
            <div className="heading text-sm font-bold tracking-tight leading-none">COMPARE</div>
            <div className="overline mt-0.5 leading-none">side-by-side sentiment</div>
          </div>
        </div>
        <ArrowsLeftRight size={18} weight="duotone" color="#FFCC00" />
      </header>

      <main className="px-4 md:px-6 py-6 max-w-[1100px] mx-auto">
        {loading && <div className="overline py-12 text-center">loading comparison…</div>}
        {err === "not_found" && (
          <div className="bg-surface border border-white/10 p-8 text-center" data-testid="compare-not-found">
            <div className="overline mb-2">comparison not available</div>
            <p className="text-sm text-white/70">One of the symbols ({symA}, {symB}) isn't in our coverage universe yet.</p>
          </div>
        )}
        {err === "missing" && (
          <div className="bg-surface border border-white/10 p-8 text-center">
            <p className="text-sm text-white/70">Provide two symbols to compare, e.g. <code>/compare/AAPL-vs-MSFT</code></p>
          </div>
        )}

        {data && a && b && (
          <>
            <section className="bg-surface border border-white/10 p-6 mb-5 text-center" data-testid="compare-hero">
              <div className="overline mb-2">composite sentiment · 0–100</div>
              <div className="grid grid-cols-3 items-center gap-2">
                <Side data={a} sym={symA} winner={winner === symA} />
                <div className="flex flex-col items-center">
                  <span className="mono text-[10px] uppercase tracking-[0.16em] text-white/40">vs</span>
                  <span className="mono text-3xl font-bold text-white/30 mt-1">Δ {data.delta}</span>
                  {winner && (
                    <div className="flex items-center gap-1 mt-2">
                      <Trophy size={14} weight="fill" color="#FFCC00" />
                      <span className="mono text-[10px] uppercase tracking-[0.18em] text-neutral_zone">winner</span>
                    </div>
                  )}
                </div>
                <Side data={b} sym={symB} winner={winner === symB} />
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailCard data={a} sym={symA} onOpen={() => navigate(`/asset/${encodeURIComponent(symA)}`)} testid="compare-detail-a" />
              <DetailCard data={b} sym={symB} onOpen={() => navigate(`/asset/${encodeURIComponent(symB)}`)} testid="compare-detail-b" />
            </section>
          </>
        )}

        <footer className="border-t border-white/10 pt-4 mt-8 overline">
          comparisons are live · not financial advice
        </footer>
      </main>
    </div>
  );
}

function Side({ data, sym, winner }) {
  const score = Math.round(data?.composite || 0);
  return (
    <div className="flex flex-col items-center" data-testid={`compare-side-${sym}`}>
      <span className="heading text-2xl md:text-3xl font-bold tracking-tight">{sym}</span>
      <span className="text-sm text-white/65 truncate max-w-[180px]">{data?.asset?.name || sym}</span>
      <span
        className="mono text-5xl md:text-6xl font-bold mt-3"
        style={{ color: labelColor(data?.composite) }}
      >
        {score}
      </span>
      <span className="mono text-xs mt-1" style={{ color: labelColor(data?.composite) }}>
        {data?.label || "—"}
      </span>
      {winner && (
        <span className="mt-2 mono text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 bg-neutral_zone text-black" data-testid="compare-winner">
          ↑ {sym} leads
        </span>
      )}
    </div>
  );
}

function DetailCard({ data, sym, onOpen, testid }) {
  const breakdown = data?.breakdown || {};
  const rows = [
    { label: "Technical", v: breakdown.technical },
    { label: "Momentum", v: breakdown.momentum },
    { label: "News", v: breakdown.news },
    { label: "Volatility", v: breakdown.volatility },
  ];
  return (
    <article className="bg-surface border border-white/10 p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="heading text-lg font-semibold">{sym}</h3>
        <button onClick={onOpen} className="overline hover:text-white">full details →</button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center gap-3 text-sm">
            <span className="overline w-20">{r.label}</span>
            <div className="flex-1 h-1.5 bg-white/5">
              {r.v != null && (
                <div className="h-full" style={{ width: `${Math.min(100, Math.max(0, r.v))}%`, background: labelColor(r.v) }} />
              )}
            </div>
            <span className="mono text-xs w-10 text-right" style={{ color: labelColor(r.v) }}>
              {r.v != null ? Math.round(r.v) : "—"}
            </span>
          </div>
        ))}
      </div>
      {data?.quote && (
        <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-white/5">
          <span className="mono text-sm">${data.quote.price?.toFixed(2)}</span>
          {data.quote.change_pct != null && (
            <span className="mono text-sm" style={{ color: data.quote.change_pct >= 0 ? "#34C759" : "#FF3B30" }}>
              {data.quote.change_pct >= 0 ? "+" : ""}{data.quote.change_pct.toFixed(2)}%
            </span>
          )}
        </div>
      )}
    </article>
  );
}
